import React from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownMenu, DropdownItem, DropdownToggle, Input } from "reactstrap";
import { packageStatus, meliStatusLabels, meliStatusClass } from "../../utils/packageStatus";
import { iconList } from "../../utils/iconList";
import Divider from "../divider";
import { roleRoutes } from "../../utils/roles";
import { canCancelOrder, canEditOrder } from "../../utils/orderActions";
import dayjs from "dayjs";
import { RelativeTime } from "../ui/RelativeTime";
import { CopyButton } from "../ui/CopyButton";

/**
 * Lista moderna de pedidos en formato tabla (filas alineadas por columnas),
 * inspirada en el mockup. Las columnas se adaptan al rol (admin / holding /
 * cliente) y se preserva exactamente la información que ya se mostraba.
 */
export default function ModernOrdersList({
  data,
  completeData,
  checkedPackage,
  addingPackage,
  allChecked,
  addAllPackges,
  showActions,
  toggle,
  setSelectedPackage,
  setVoucherModal,
  setCancelPackage,
  setEditOrder,
  setPackageEvidence,
  setViewEvidence,
  user,
}) {
  const { t } = useTranslation();
  const isAdmin = user?.role === roleRoutes.admin || user?.role === roleRoutes.support;
  const isClient = user?.role === roleRoutes.client;
  const isHolding = user?.role === roleRoutes.holdingAdmin;
  // Superadmin del sistema: ve pedidos de TODOS los operadores, así que es el
  // único que necesita las columnas del admin MÁS "Empresa logística" — sin
  // ella no hay forma de saber de quién es cada pedido.
  const isPlatform = user?.role === roleRoutes.systemSuperadmin;

  // Columnas dependientes del rol (deben coincidir con el grid del SCSS).
  const showComercio = isAdmin || isHolding || isPlatform;
  const showRepartidor = isAdmin || isPlatform;
  const showLogistica = isClient || isPlatform;
  const roleClass = isPlatform
    ? "mol--platform"
    : isClient
      ? "mol--client"
      : isHolding
        ? "mol--holding"
        : "mol--admin";

  // Índices O(1) para no hacer .find() por fila (antes O(n²) en cada render con
  // listas largas: provocaba el lag al interactuar con la tabla). Se preserva la
  // semántica de Array.find: la PRIMERA coincidencia por followingNumber gana.
  const completeByNumber = React.useMemo(() => {
    const map = new Map();
    (completeData || []).forEach((f) => {
      if (!map.has(f?.followingNumber)) map.set(f?.followingNumber, f);
    });
    return map;
  }, [completeData]);

  const checkedNumbers = React.useMemo(() => {
    const set = new Set();
    (checkedPackage || []).forEach((f) => set.add(f?.followingNumber));
    return set;
  }, [checkedPackage]);

  // Si no hay datos, mostrar mensaje
  if (!data || data.length === 0) {
    return (
      <div className="modern-orders-empty">
        <svg className="modern-orders-empty__icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-2h2v-2h-2v2zm0-4h2V7h-2v6z"/>
        </svg>
        <h3 className="modern-orders-empty__title">{t("No hay pedidos para mostrar")}</h3>
        <p className="modern-orders-empty__description">
          {t("Intenta ajustar los filtros o busca con otros criterios")}
        </p>
      </div>
    );
  }

  // Función para obtener la clase de estado
  const getStatusClass = (status) => {
    const statusMap = {
      0: 'pending',
      1: 'picked',
      2: 'picked',
      3: 'route',
      4: 'delivered',
      5: 'cancelled',
      6: 'failed',
      7: 'failed',
      30: 'route',
      100: 'storage',
      // Rama de logística inversa (packageStatus.js): el tono reverse (violeta)
      // marca "esto es una devolución"; la fase la distingue el label.
      21: 'return',
      22: 'return',
      31: 'return',
      41: 'return'
    };
    return `status-badge--${statusMap[status] || 'pending'}`;
  };

  // Función para obtener el paquete completo (lookup O(1) vía índice memoizado)
  const getFullPackage = (item) =>
    completeByNumber.get(item.correlative?.comparative);

  // Función para obtener el texto del tipo de paquete
  const getPackageTypeLabel = (type) => {
    const typeLabelMap = {
      'EXCEL': 'Excel',
      'FALABELLA': 'Falabella',
      'FALABELLA DIRECTO': 'Falabella Directo',
      'RIPLEY': 'Ripley',
      'WOOCOMMERCE': 'WooCommerce',
      'PRESTASHOP': 'Prestashop',
      'SENDU': 'Sendu',
      'OTS': 'Manual', // pedidos creados a mano: type "OTS" se muestra como "Manual"
      'MERCADOLIBRE': 'MercadoLibre',
      'SHOPIFY': 'Shopify',
      'JUMPSELLER': 'Jumpseller',
      'WIX': 'Wix',
      'PARIS': 'Paris',
    };
    return typeLabelMap[type?.toUpperCase()] || t('Desconocido');
  };

  // Función para renderizar el ícono según el tipo de paquete
  const renderPackageTypeIcon = (type) => {
    const typeIconMap = {
      'EXCEL': 'file-excel',
      'FALABELLA': 'falabella',
      'FALABELLA DIRECTO': 'falabella',
      'RIPLEY': 'ripley',
      'WOOCOMMERCE': 'woo',
      'PRESTASHOP': 'prestashop',
      'SENDU': 'sendu',
      'OTS': 'box',
      'MERCADOLIBRE': 'ml',
      'SHOPIFY': 'shopify',
      'JUMPSELLER': 'jumpsellerIcon',
      'WIX': 'wix',
      'PARIS': 'paris',
    };

    const iconKey = typeIconMap[type?.toUpperCase()] || 'box';

    // Para Excel usamos Font Awesome
    if (type?.toUpperCase() === 'EXCEL') {
      return (
        <i
          className="fas fa-file-excel modern-orders-list__excel-icon"
          title={getPackageTypeLabel(type)}
        />
      );
    }

    // Para otros tipos, usar los iconos del iconList
    const iconPath = iconList[iconKey] || iconList.box;
    return (
      <img
        src={iconPath}
        alt={getPackageTypeLabel(type)}
        className="modern-orders-list__type-icon"
        title={getPackageTypeLabel(type)}
      />
    );
  };

  // Clics sobre estos elementos conservan su propio comportamiento y no abren
  // el detalle (el correlativo es role="button" y ya lo abre por sí mismo).
  const ROW_CLICK_IGNORE =
    'input, button, a, label, select, textarea, [role="button"], [role="menu"], [role="menuitem"], .dropdown, .dropdown-menu, .modern-orders-list__checkbox-wrapper, .modern-orders-list__actions-column, [data-row-click-ignore]';

  // La fila entera abre el detalle: antes solo el correlativo respondía y
  // Clarity registraba "dead clicks" en el resto de la fila (que resalta al
  // pasar el mouse). Se ignora el clic si cae en un control interactivo, si hay
  // un menú de acciones abierto (reactstrap lo cierra con el clic de documento
  // y la fila no debe abrir el modal a la vez) o si el usuario está
  // seleccionando texto dentro de la fila.
  const handleRowClick = (e, item) => {
    if (typeof setPackageEvidence !== 'function' || typeof setViewEvidence !== 'function') return;
    if (e.target.closest(ROW_CLICK_IGNORE)) return;
    if (showActions?.some((a) => a.actions)) return;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && e.currentTarget.contains(sel.anchorNode)) return;
    setPackageEvidence(item);
    setViewEvidence(true);
  };

  // Extrae texto de un nodo "d-grid" (span[0]=principal, span[1]=secundario)
  const nodeLine = (node, idx) => node?.props?.children?.[idx]?.props?.children;

  return (
    <div className={`modern-orders-list-wrapper ${roleClass}`} role="region" aria-label={t("Lista de pedidos")}>
      <div className="modern-orders-list" role="table" aria-label={`${data?.length || 0} pedidos`}>
        {/* Encabezado de columnas */}
        <div className="modern-orders-list__head" role="row">
          <div className="modern-orders-list__head-cell modern-orders-list__checkbox-wrapper">
            <Input
              type="checkbox"
              id="select-all-orders"
              className="modern-orders-list__checkbox"
              onClick={(e) => addAllPackges(e.target.checked)}
              onChange={(e) => addAllPackges(e.target.checked)}
              checked={allChecked}
              aria-label={t("Seleccionar todos los pedidos")}
            />
          </div>
          <div className="modern-orders-list__head-cell">{t("Pedido")}</div>
          {showComercio && <div className="modern-orders-list__head-cell">{t("Comercio")}</div>}
          <div className="modern-orders-list__head-cell">{t("Destinatario")}</div>
          <div className="modern-orders-list__head-cell">{t("Dirección de entrega")}</div>
          <div className="modern-orders-list__head-cell">{t("Estado")}</div>
          {showRepartidor && <div className="modern-orders-list__head-cell">{t("Repartidor")}</div>}
          {showLogistica && <div className="modern-orders-list__head-cell">{t("Empresa logística")}</div>}
          <div className="modern-orders-list__head-cell">{t("Actualizado")}</div>
          <div className="modern-orders-list__head-cell" aria-hidden="true"> </div>
        </div>

        {/* Filas */}
        {data?.map((item, index) => {
          const fullPackage = getFullPackage(item);
          // Texto del chip de estado calculado una vez: se usa en el label y en
          // el title (tooltip nativo con el texto completo cuando hay ellipsis).
          const statusLabel =
            fullPackage?.status != null && packageStatus[fullPackage.status]
              ? t(packageStatus[fullPackage.status])
              : packageStatus[fullPackage?.status];
          const isChecked = checkedNumbers.has(item.correlative?.comparative);
          const isDropdownOpen = showActions?.find(
            (el) => el.correlative?.comparative === item?.correlative?.comparative
          )?.actions;
          const recNode = item.receiver || item.client;
          const recName = nodeLine(recNode, 0);
          const recPhone = nodeLine(recNode, 1);
          const addrLine = nodeLine(item.destiny, 0);
          const addrCity = nodeLine(item.destiny, 1);

          return (
            <div
              key={item.correlative?.comparative ?? index}
              role="row"
              className={`modern-orders-list__item ${isChecked ? 'modern-orders-list__item--selected' : ''} ${isDropdownOpen ? 'has-dropdown-open' : ''}`}
              aria-label={`Pedido ${item.correlative?.comparative}`}
              onClick={(e) => handleRowClick(e, item)}
            >
              {/* Checkbox */}
              <div className="modern-orders-list__cell modern-orders-list__checkbox-wrapper">
                <Input
                  type="checkbox"
                  className="modern-orders-list__checkbox"
                  onChange={() => addingPackage(item)}
                  checked={!!isChecked}
                  aria-label={`Seleccionar pedido ${item.correlative?.comparative}`}
                />
              </div>

              {/* Pedido: tipo + correlativo + venta */}
              <div className="modern-orders-list__cell">
                <div className="modern-orders-list__correlative-container">
                  {item.packageType && renderPackageTypeIcon(item.packageType)}
                  <span className="inline-flex items-center gap-0.5">
                  <span
                    className="modern-orders-list__correlative"
                    onClick={() => {
                      setPackageEvidence(item);
                      setViewEvidence(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setPackageEvidence(item);
                        setViewEvidence(true);
                      }
                    }}
                    aria-label={`Ver evidencia del pedido ${item.correlative?.comparative}`}
                  >
                    #{item.correlative?.comparative}
                  </span>
                  <CopyButton
                    value={item.correlative?.comparative}
                    label={t('Copiar número de seguimiento')}
                    copiedMessage={t('Número de seguimiento copiado')}
                  />
                  </span>
                </div>
                {item.orderId && (
                  // El botón va fuera de __subline: esa línea trunca con ellipsis y un id largo
                  // (MercadoLibre) recortaba justo el botón.
                  <span className="flex items-center gap-0.5 min-w-0">
                    <span className="modern-orders-list__subline">
                      {t('Venta')}{' '}
                      {typeof item.orderId === 'object'
                        ? item.orderId.props?.children?.props?.children
                        : item.orderId}
                    </span>
                    {/* orderId puede ser un nodo JSX: se copia el order_id crudo del pedido completo */}
                    <CopyButton
                      value={fullPackage?.order_id ?? (typeof item.orderId === 'object' ? null : item.orderId)}
                      label={t('Copiar número de venta')}
                      copiedMessage={t('Número de venta copiado')}
                      className="shrink-0"
                    />
                  </span>
                )}
              </div>

              {/* Comercio */}
              {showComercio && (
                <div className="modern-orders-list__cell">
                  <span className="modern-orders-list__cell-value" title={item.business || ''}>
                    {item.business || t('Sin información')}
                  </span>
                </div>
              )}

              {/* Destinatario */}
              <div className="modern-orders-list__cell">
                {recName || recPhone ? (
                  <div className="modern-orders-list__receiver-info">
                    {recName && (
                      <span className="modern-orders-list__receiver-name" title={recName}>{recName}</span>
                    )}
                    {recPhone && (
                      <span className="modern-orders-list__receiver-phone">{recPhone}</span>
                    )}
                  </div>
                ) : (
                  <span className="modern-orders-list__muted">—</span>
                )}
              </div>

              {/* Dirección de entrega */}
              <div className="modern-orders-list__cell">
                {addrLine || addrCity ? (
                  <>
                    {addrLine && (
                      <span className="modern-orders-list__cell-value" title={addrLine}>{addrLine}</span>
                    )}
                    {addrCity && (
                      <span className="modern-orders-list__subline">{addrCity}</span>
                    )}
                  </>
                ) : (
                  <span className="modern-orders-list__muted">—</span>
                )}
              </div>

              {/* Estado */}
              <div className="modern-orders-list__cell modern-orders-list__cell--badges">
                <div className="modern-orders-list__badges-container">
                  {/* span interno necesario: text-overflow:ellipsis no aplica al
                      texto anonimo de un contenedor flex; sin el, chips largos
                      ("En bodega (devolucion)") desbordaban la celda ESTADO y
                      pisaban la columna vecina a 1280-1366px */}
                  <div
                    className={`status-badge ${getStatusClass(fullPackage?.status)}`}
                    title={statusLabel}
                  >
                    <span className="status-badge__label">{statusLabel}</span>
                  </div>
                  {/* Semáforo de incidencia activa */}
                  {fullPackage?.incidentId && !fullPackage?.incidentResolvedAt && (() => {
                    const hours = dayjs().diff(dayjs(fullPackage.incidentDate), 'hours', true);
                    const color = hours < 3 ? '#22c55e' : hours < 6 ? '#eab308' : '#ef4444';
                    const hh = Math.floor(hours);
                    const mm = Math.floor((hours % 1) * 60);
                    return (
                      <div
                        className="incident-semaphore"
                        style={{ backgroundColor: color }}
                        title={`Incidencia abierta hace ${hh}h ${mm}m`}
                      />
                    );
                  })()}
                  {/* Badge de estado MercadoLibre */}
                  {fullPackage?.type?.toUpperCase() === 'MERCADOLIBRE' &&
                   fullPackage?.meliStatus?.status != null && (
                    <div
                      className={`meli-badge ${meliStatusClass[fullPackage.meliStatus.status]}`}
                      title={`Estado en MercadoLibre: ${meliStatusLabels[fullPackage.meliStatus.status]}`}
                    >
                      <img src={iconList.ml} className="meli-badge__logo dark-safe-img" alt="MercadoLibre" />
                      {t(meliStatusLabels[fullPackage.meliStatus.status])}
                    </div>
                  )}
                  {/* Badge de sobredimensionado */}
                  {fullPackage?.overSizedEvidence &&
                   (fullPackage.overSizedEvidence.reason ||
                    (fullPackage.overSizedEvidence.images && fullPackage.overSizedEvidence.images.length > 0)) && (
                    <div className="status-badge status-badge--oversized">
                      {fullPackage.overSizedEvidence.category ||
                        (fullPackage.overSizedEvidence.reason ? 'XL' : 'XL')}
                    </div>
                  )}
                </div>
              </div>

              {/* Repartidor (admin / support) */}
              {showRepartidor && (
                <div className="modern-orders-list__cell">
                  {item.deliveryMan ? (
                    <div className="modern-orders-list__delivery-info">
                      {item.deliveryMan.icon && (
                        <img
                          src={item.deliveryMan.icon || iconList["defaultAvatar"]}
                          className="modern-orders-list__delivery-avatar dark-safe-img"
                          alt="avatar"
                        />
                      )}
                      <span className="modern-orders-list__delivery-name" title={item.deliveryMan.name}>
                        {item.deliveryMan.name}
                      </span>
                    </div>
                  ) : (
                    <span className="modern-orders-list__muted">—</span>
                  )}
                </div>
              )}

              {/* Empresa logística (cliente y superadmin del sistema) */}
              {showLogistica && (
                <div className="modern-orders-list__cell">
                  <span className="modern-orders-list__cell-value">
                    {fullPackage?.currentCarrier?.carrierType === "commerce_fleet"
                      ? (fullPackage?.senderInfo?.businessName || "N/A")
                      : (fullPackage?.deliveryInfo?.deliveryBusinessName
                        || (typeof item.logistics === 'object'
                          ? item.logistics.props?.children?.props?.children
                          : item.logistics))}
                    {fullPackage?.currentCarrier?.carrierType === "commerce_fleet" && (
                      <span className="badge-fleet-own">{t('Flota propia')}</span>
                    )}
                  </span>
                </div>
              )}

              {/* Actualizado / Creado */}
              <div className="modern-orders-list__cell">
                {fullPackage?.updatedAt && (
                  <RelativeTime
                    value={fullPackage.updatedAt}
                    live
                    t={t}
                    className="modern-orders-list__cell-value"
                  />
                )}
                {fullPackage?.createdAt && (
                  <span className="modern-orders-list__subline">
                    {t('Creado')} {dayjs(fullPackage.createdAt).format("DD/MM/YY")}
                  </span>
                )}
              </div>

              {/* Acciones */}
              <div className="modern-orders-list__cell modern-orders-list__actions-column">
                <div className="modern-orders-list__actions-menu">
                  {isClient ? (
                    <Dropdown
                      isOpen={isDropdownOpen}
                      toggle={() => toggle(item)}
                      direction="down"
                      className="dropdown-new-style"
                    >
                      <DropdownToggle tag="div" className="modern-orders-list__actions-button">
                        <img src={iconList["actions"]} alt="Acciones" />
                      </DropdownToggle>
                      <DropdownMenu end className="custom-dropdown-menu-opaque">
                        <DropdownItem
                          className="dropdown-font mb-1"
                          onClick={() => {
                            setSelectedPackage(item);
                            setVoucherModal(true);
                          }}
                        >
                          <div className="flex items-center">
                            <img
                              className="dropdown-font__image mr-2"
                              alt="detalle"
                              src={iconList.qrcode}
                            />
                            {t('Ver detalle')}
                          </div>
                        </DropdownItem>
                        <Divider />
                        <DropdownItem
                          className="dropdown-font mt-1"
                          disabled={!canCancelOrder(user?.role, item?.status?.status)}
                          onClick={() => {
                            setSelectedPackage(item);
                            setCancelPackage(true);
                          }}
                        >
                          <div className="flex items-center">
                            <img
                              className="dropdown-font__image mr-2"
                              alt="cancelar"
                              src={iconList.close}
                            />
                            {t('Cancelar')}
                          </div>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  ) : (
                    <Dropdown
                      isOpen={isDropdownOpen}
                      toggle={() => toggle(item)}
                      direction="down"
                      className="dropdown-new-style"
                    >
                      <DropdownToggle tag="div" className="modern-orders-list__actions-button">
                        <img src={iconList["actions"]} alt="Acciones" />
                      </DropdownToggle>
                      <DropdownMenu end className="custom-dropdown-menu-opaque">
                        <DropdownItem
                          className="dropdown-font mb-1"
                          onClick={() => {
                            setSelectedPackage(item);
                            setVoucherModal(true);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <img
                              className="dropdown-font__image mr-2"
                              alt="etiqueta"
                              src={iconList.qrcode}
                            />
                            {t('Etiqueta')}
                          </div>
                        </DropdownItem>
                        <Divider />
                        <DropdownItem
                          className="dropdown-font mt-1"
                          disabled={!canEditOrder(user?.role, item?.status?.value)}
                          onClick={() => {
                            setSelectedPackage(item);
                            setEditOrder(true);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <img
                              className="dropdown-font__image mr-2"
                              alt="editar"
                              src={iconList.edit}
                            />
                            {t('Editar pedido')}
                          </div>
                        </DropdownItem>
                        <Divider />
                        <DropdownItem
                          className="dropdown-font mt-1"
                          disabled={!canCancelOrder(user?.role, item?.status?.value)}
                          onClick={() => {
                            setSelectedPackage(item);
                            setCancelPackage(true);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <img
                              className="dropdown-font__image mr-2"
                              alt="cancelar"
                              src={iconList.close}
                            />
                            {t('Cancelar')}
                          </div>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
